import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBarcode, FaShoppingCart, FaTrash, FaPlus, FaMinus, FaSearch, FaCheckCircle, FaMoneyBillWave, FaCreditCard, FaMobileAlt, FaLayerGroup, FaBoxOpen, FaChevronLeft, FaChevronRight, FaGift, FaUndo } from 'react-icons/fa';
import { IoClose, IoInformationCircle } from 'react-icons/io5';
import {
    lookupBarcode,
    createWalkInOrder,
    fetchGstGroups,
    validateCartStock,
    getCoupons,
    getCategories,
    getPosBillingProducts,
    pipGetConfig,
    pipUpdateConfig,
    searchPosCustomerMobiles,
    evaluatePosOffers,
    getScopedBranchIdForUser,
    lookupWalkInOrder,
    listWalkInOrdersForReturn,
    returnWalkInOrderItems,
} from '../../services/ClientService';
import { printToThermalPrinter } from '../../utils/thermalPrinter';
import MerchantBillingReturnsPanel from './MerchantBillingReturnsPanel';

// Format numbers using the Indian numbering system (e.g. 154587 -> 1,54,587.00)
const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0.00';
    const sign = Number(num) < 0 ? '-' : '';
    const n = Math.abs(Number(num) || 0).toFixed(2);
    let [intPart, decPart] = n.split('.');
    if (intPart.length > 3) {
        const last3 = intPart.slice(-3);
        let rest = intPart.slice(0, -3);
        rest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
        intPart = rest + ',' + last3;
    }
    return `${sign}${intPart}.${decPart}`;
};

const NO_IMAGE = require('../../no-image-icon-23494.png');
const FIND_ITEM_PAGE_SIZE = 24;

const getProductImage = (product) => {
    if (product?.images?.length && (product.images[0]?.preview || product.images[0]?.url)) {
        return product.images[0].preview || product.images[0].url;
    }
    const firstOption = product?.productOptions?.[0];
    if (firstOption?.images?.length && (firstOption.images[0]?.preview || firstOption.images[0]?.url)) {
        return firstOption.images[0].preview || firstOption.images[0].url;
    }
    return NO_IMAGE;
};

/** Swap once to the bundled no-image asset — never re-request a missing public file. */
const handleProductImageError = (e) => {
    const img = e.currentTarget;
    if (!img || img.dataset.fallbackApplied === '1') {
        if (img) img.onerror = null;
        return;
    }
    img.dataset.fallbackApplied = '1';
    img.onerror = null;
    img.src = NO_IMAGE;
};

const getDisplayPrice = (product) => {
    const opts = product?.productOptions || [];
    if (!opts.length) return 0;
    const prices = opts.map((o) => o.discountPrice || o.originalPrice || 0).filter((p) => p > 0);
    if (!prices.length) return 0;
    return Math.min(...prices);
};

const getDisplayStock = (product) => {
    const opts = product?.productOptions || [];
    if (product?.commonInventoryType === true) {
        // Shared pool in primary metric — options usually carry the same qty; take the max
        return opts.reduce((max, o) => Math.max(max, Number(o.availableQuantity) || 0), 0);
    }
    return opts.reduce((sum, o) => sum + (Number(o.availableQuantity) || 0), 0);
};

/** Loyalty progress that only asks for mobile — hide in Classic (Behaviour OFF). */
const isEnterMobileLoyaltyNudge = (p) => {
    if (!p) return false;
    if (p.offerId === 'mobile-nudge' || p.ruleType === 'nudge') return true;
    const msg = String(p.message || '');
    return /^Enter mobile\b/i.test(msg)
        || /Enter the customer'?s 10-digit mobile/i.test(msg);
};

const getPrimaryMetricLabel = (product) => (
    product?.mainMetric?.metricLabel
    || product?.mainMetric?.label
    || 'Kg'
);

const formatStockDisplay = (product) => {
    const stock = getDisplayStock(product);
    if (product?.commonInventoryType === true) {
        const qty = Number.isInteger(stock) ? String(stock) : parseFloat(Number(stock).toFixed(3)).toString();
        return `Stock: ${qty} ${getPrimaryMetricLabel(product)}`;
    }
    return `Stock: ${stock}`;
};

const getOptionLabel = (option) => (
    option?.weight?.weightLabel
    || option?.weightLabel
    || (typeof option?.weight === 'string' ? option.weight : null)
    || option?.label
    || 'Option'
);

const MerchantBilling = () => {
    const [barcode, setBarcode] = useState('');
    const [cart, setCart] = useState([]);
    const [isLookingUp, setIsLookingUp] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [isSnackBarVisible, setIsSnackBarVisible] = useState(false);
    const [snackBarClass, setSnackBarClass] = useState('bg-green-500 text-white');
    const [isProcessingOrder, setIsProcessingOrder] = useState(false);
    const [showProductModal, setShowProductModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [gstGroupsMap, setGstGroupsMap] = useState({});
    const barcodeInputRef = useRef(null);

    // Coupon related states
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [showCouponModal, setShowCouponModal] = useState(false);
    const [couponDiscount, setCouponDiscount] = useState(0);

    // Payment method state
    const [paymentMethod, setPaymentMethod] = useState(null); // null, 'Cash', 'Card', 'UPI'

    // Behaviour Payment — when ON: apply field-required rules from Customer Analytics config
    const [behaviourPaymentEnabled, setBehaviourPaymentEnabled] = useState(false);
    const [behaviourForcedOn, setBehaviourForcedOn] = useState(true);
    const [behaviourRequireMobile, setBehaviourRequireMobile] = useState(true);
    const [behaviourRequireCustomerName, setBehaviourRequireCustomerName] = useState(true);
    const [togglingBehaviour, setTogglingBehaviour] = useState(false);
    const [customerMobile, setCustomerMobile] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [mobileSuggestions, setMobileSuggestions] = useState([]);
    const [showMobileSuggestions, setShowMobileSuggestions] = useState(false);
    const [loadingMobileSuggestions, setLoadingMobileSuggestions] = useState(false);
    const mobileSuggestReqRef = useRef(0);

    // POS Offers (loyalty) evaluation
    const [posOfferEval, setPosOfferEval] = useState({ applied: null, progress: [], unlocked: [] });
    const [offerDiscount, setOfferDiscount] = useState(0);

    // Billing | Returns mode
    const [posWorkspaceMode, setPosWorkspaceMode] = useState('billing'); // 'billing' | 'returns'
    const [returnSearch, setReturnSearch] = useState('');
    const [returnLookupLoading, setReturnLookupLoading] = useState(false);
    const [returnBills, setReturnBills] = useState([]);
    const [selectedReturnBill, setSelectedReturnBill] = useState(null);
    const [returnQtyByIndex, setReturnQtyByIndex] = useState({});
    const [returnReason, setReturnReason] = useState(''); // 'just_return' | 'damaged'
    const [returnNote, setReturnNote] = useState('');
    const [returnSubmitting, setReturnSubmitting] = useState(false);
    const [recentReturnBills, setRecentReturnBills] = useState([]);
    const [recentReturnBillsLoading, setRecentReturnBillsLoading] = useState(false);
    const [recentReturnLookback, setRecentReturnLookback] = useState('today');
    const [recentReturnLookbackLabel, setRecentReturnLookbackLabel] = useState('Today');
    const [recentReturnPagination, setRecentReturnPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNextPage: false,
        hasPrevPage: false,
    });
    const [returnStartTime, setReturnStartTime] = useState('');
    const [returnEndTime, setReturnEndTime] = useState('');
    const [appliedReturnStartTime, setAppliedReturnStartTime] = useState('');
    const [appliedReturnEndTime, setAppliedReturnEndTime] = useState('');

    // Find Item modal (catalogue browse by name / category / subcategory)
    const [showFindItemModal, setShowFindItemModal] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedParentId, setSelectedParentId] = useState(null);
    const [selectedSubId, setSelectedSubId] = useState(null);
    const [findItemSearch, setFindItemSearch] = useState('');
    const [debouncedFindSearch, setDebouncedFindSearch] = useState('');
    const [findItemProducts, setFindItemProducts] = useState([]);
    const [findItemTotal, setFindItemTotal] = useState(0);
    const [findItemPage, setFindItemPage] = useState(0);
    const [loadingFindItems, setLoadingFindItems] = useState(false);
    const findItemSearchRef = useRef(null);

    const activeCategoryId = selectedSubId || selectedParentId || null;
    const activeParent = categories.find((c) => String(c._id) === String(selectedParentId)) || null;
    const activeSubcategories = Array.isArray(activeParent?.subcategories) ? activeParent.subcategories : [];
    const findItemTotalPages = Math.max(1, Math.ceil(findItemTotal / FIND_ITEM_PAGE_SIZE));

    // Get user role from localStorage
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const isMasterAccount = userData.masterAccount === true;
    const isWarehouse = userData.isWarehouse === true;

    // Auto-focus barcode input on mount
    useEffect(() => {
        barcodeInputRef.current?.focus();

        // fetch gst groups
        (async () => {
            try {
                const res = await fetchGstGroups();
                if (res && res.success && res.data && res.data.length) {
                    // backend returns an array of documents; gstGroups is inside data[0].gstGroups
                    const groupsArray = (res.data[0] && res.data[0].gstGroups) || [];
                    const map = {};
                    groupsArray.forEach(g => {
                        map[g._id] = g;
                    });
                    setGstGroupsMap(map);
                }
            } catch (e) {
                console.warn('Failed to fetch GST groups', e);
            }
        })();

        // Fetch Offline coupons for merchant billing
        (async () => {
            try {
                const response = await getCoupons({ storeType: 'offline', isActive: [true] });
                if (response.success && response.data) {
                    // Filter valid coupons (not expired, within date range)
                    const validCoupons = response.data.filter(coupon => {
                        const now = new Date();
                        const endDate = new Date(coupon.endDate);
                        return endDate >= now;
                    });
                    setAvailableCoupons(validCoupons);
                }
            } catch (error) {
                console.error('Failed to fetch Offline coupons:', error);
            }
        })();

        // Categories + subcategories for Find Item modal
        (async () => {
            try {
                const response = await getCategories({ includeSubcategories: true });
                if (response.success && Array.isArray(response.data)) {
                    setCategories(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch categories:', error);
            }
        })();

        // Behaviour Payment toggle state
        (async () => {
            try {
                const res = await pipGetConfig({});
                if (res?.success && res.data) {
                    const forced = res.data.behaviourForcedOn !== false;
                    setBehaviourForcedOn(forced);
                    setBehaviourPaymentEnabled(forced ? true : !!res.data.behaviourPaymentEnabled);
                    setBehaviourRequireMobile(res.data.behaviourRequireMobile !== false);
                    setBehaviourRequireCustomerName(res.data.behaviourRequireCustomerName !== false);
                }
            } catch (e) {
                console.warn('Behaviour config load skipped', e);
            }
        })();
    }, []);

    // Debounced POS mobile autocomplete (Order Summary — loyalty / Behaviour)
    useEffect(() => {
        const digits = String(customerMobile).replace(/\D/g, '');
        if (digits.length < 3 || digits.length >= 10) {
            setMobileSuggestions([]);
            setShowMobileSuggestions(false);
            setLoadingMobileSuggestions(false);
            return undefined;
        }
        setLoadingMobileSuggestions(true);
        const reqId = ++mobileSuggestReqRef.current;
        const t = setTimeout(async () => {
            try {
                const res = await searchPosCustomerMobiles({ query: digits, limit: 8 });
                if (reqId !== mobileSuggestReqRef.current) return;
                const list = Array.isArray(res?.data) ? res.data : [];
                setMobileSuggestions(list);
                setShowMobileSuggestions(list.length > 0);
            } catch {
                if (reqId !== mobileSuggestReqRef.current) return;
                setMobileSuggestions([]);
                setShowMobileSuggestions(false);
            } finally {
                if (reqId === mobileSuggestReqRef.current) setLoadingMobileSuggestions(false);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [customerMobile]);

    // Evaluate POS loyalty offers when paid cart / mobile / payment changes (ignore free-gift lines)
    useEffect(() => {
        const paidCart = cart.filter((item) => !item.isFreeOfferItem);
        if (paidCart.length === 0) {
            setPosOfferEval({ applied: null, progress: [], unlocked: [] });
            setOfferDiscount(0);
            // Drop orphan free gifts when no paid items left
            if (cart.some((item) => item.isFreeOfferItem)) {
                setCart((prev) => prev.filter((item) => !item.isFreeOfferItem));
            }
            return undefined;
        }
        const subtotal = paidCart.reduce((sum, item) => sum + (item.total || 0), 0);
        const mobile = String(customerMobile || '').replace(/\D/g, '').slice(-10);
        let cancelled = false;
        const delay = mobile.length === 10 ? 150 : 400;
        const t = setTimeout(async () => {
            try {
                const res = await evaluatePosOffers({
                    branchId: getScopedBranchIdForUser()
                        || (() => {
                            try {
                                return JSON.parse(localStorage.getItem('userData') || '{}')?._id || null;
                            } catch {
                                return null;
                            }
                        })(),
                    mobile: mobile.length === 10 ? mobile : String(customerMobile || '').replace(/\D/g, ''),
                    paymentMethod,
                    cartSubtotal: subtotal,
                    cartItems: paidCart.map((item) => ({
                        productId: item._id,
                        quantity: item.quantity,
                        categoryId: item.categoryId || null,
                        subcategoryId: item.subcategoryId || null,
                        total: item.total,
                    })),
                });
                if (cancelled) return;
                if (!res?.success) {
                    console.warn('POS offer evaluate failed', res);
                    setPosOfferEval({ applied: null, progress: [], unlocked: [] });
                    setOfferDiscount(0);
                    return;
                }
                const data = res.data || {};
                const progress = Array.isArray(data.progress) ? data.progress : [];
                const unlocked = Array.isArray(data.unlocked) ? data.unlocked : [];
                const applied = data.applied || null;
                console.log('POS offer evaluate', {
                    mobile,
                    offersMatched: data.offersMatched,
                    applied,
                    progress,
                    unlocked,
                });
                setPosOfferEval({ applied, progress, unlocked });
                if (applied && Number(applied.discount) > 0) {
                    const stacks = !!applied.stackWithCoupon;
                    if (selectedCoupon && !stacks) {
                        setOfferDiscount(0);
                    } else {
                        setOfferDiscount(Number(applied.discount) || 0);
                    }
                } else {
                    setOfferDiscount(0);
                }
            } catch (err) {
                console.warn('POS offer evaluate error', err);
                if (!cancelled) {
                    setPosOfferEval({ applied: null, progress: [], unlocked: [] });
                    setOfferDiscount(0);
                }
            }
        }, delay);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [cart, customerMobile, paymentMethod, selectedCoupon]);

    // Auto-add / remove free gift lines from applied free_items offer
    useEffect(() => {
        const applied = posOfferEval.applied;
        const freeItems = Array.isArray(applied?.freeItems) ? applied.freeItems : [];
        const offerId = applied?.offerId ? String(applied.offerId) : null;

        setCart((prev) => {
            const paid = prev.filter((item) => !item.isFreeOfferItem);
            if (!freeItems.length || !offerId) {
                if (!prev.some((item) => item.isFreeOfferItem)) return prev;
                return paid;
            }

            const giftLines = freeItems.map((g) => {
                const selectedOption = {
                    ...(g.selectedOption || {}),
                    _id: g.selectedOption?._id || g.optionId,
                    discountPrice: 0,
                    originalPrice: Number(g.catalogPrice || g.selectedOption?.originalPrice || 0) || 0,
                    discount: Number(g.catalogPrice || g.selectedOption?.originalPrice || 0) || 0,
                };
                return {
                    _id: g.productMongoId,
                    productId: g.productId || g.productMongoId,
                    productName: g.productName || 'Free item',
                    image: g.image || '',
                    selectedOption,
                    quantity: Math.max(1, Number(g.quantity) || 1),
                    price: 0,
                    total: 0,
                    taxPerUnit: 0,
                    tax: 0,
                    gstGroup: null,
                    gstGroupId: null,
                    source: 'free_offer',
                    availableQuantity: 999999,
                    isOutOfStock: false,
                    isActive: true,
                    commonInventoryType: !!g.commonInventoryType,
                    categoryId: g.categoryId || null,
                    subcategoryId: g.subcategoryId || null,
                    isFreeOfferItem: true,
                    freeOfferId: offerId,
                    freeCatalogPrice: Number(g.catalogPrice) || 0,
                    freeOptionLabel: g.optionLabel || '',
                };
            });

            const sig = (items) => items
                .filter((i) => i.isFreeOfferItem)
                .map((i) => `${i._id}:${i.selectedOption?._id}:${i.quantity}:${i.freeOfferId}`)
                .sort()
                .join('|');
            const next = [...paid, ...giftLines];
            if (sig(prev) === sig(next) && paid.length === prev.filter((i) => !i.isFreeOfferItem).length) {
                return prev;
            }
            return next;
        });
    }, [posOfferEval.applied]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedFindSearch(findItemSearch.trim()), 400);
        return () => clearTimeout(t);
    }, [findItemSearch]);

    const loadFindItemPage = useCallback(async (pageIndex) => {
        if (!showFindItemModal) return;
        setLoadingFindItems(true);
        try {
            const res = await getPosBillingProducts({
                skip: pageIndex * FIND_ITEM_PAGE_SIZE,
                limit: FIND_ITEM_PAGE_SIZE,
                categoryId: activeCategoryId || null,
                searchTerm: debouncedFindSearch || '',
            });
            if (res?.success) {
                setFindItemProducts(res.data || []);
                setFindItemTotal(res.totalCount || 0);
                setFindItemPage(pageIndex);
            } else {
                setFindItemProducts([]);
                setFindItemTotal(0);
                setFindItemPage(0);
            }
        } catch (error) {
            console.error('Failed to fetch catalogue products:', error);
            setFindItemProducts([]);
            setFindItemTotal(0);
            setFindItemPage(0);
        } finally {
            setLoadingFindItems(false);
        }
    }, [showFindItemModal, activeCategoryId, debouncedFindSearch]);

    useEffect(() => {
        if (!showFindItemModal) return;
        loadFindItemPage(0);
    }, [loadFindItemPage, showFindItemModal]);

    useEffect(() => {
        if (showFindItemModal) {
            setTimeout(() => findItemSearchRef.current?.focus(), 80);
        }
    }, [showFindItemModal]);

    const openFindItemModal = () => {
        setShowFindItemModal(true);
    };

    const closeFindItemModal = () => {
        setShowFindItemModal(false);
        setTimeout(() => barcodeInputRef.current?.focus(), 50);
    };

    // If GST groups arrive after items were added, attach missing gstGroup info and recompute tax
    useEffect(() => {
        if (!cart.length) return;
        if (!gstGroupsMap || Object.keys(gstGroupsMap).length === 0) return;

        setCart(prevCart => prevCart.map(item => {
            if (!item.gstGroup && item.gstGroupId) {
                const g = gstGroupsMap[item.gstGroupId] || null;
                const priceValue = item.price || 0;
                const pct = g && typeof g.percentage === 'number' ? Number(g.percentage) : 0;
                let taxPerUnit = 0;
                if (g) {
                    if (g.type === 'exclusive') taxPerUnit = (priceValue * pct) / 100;
                    else taxPerUnit = (priceValue * pct) / (100 + pct);
                }
                return { ...item, gstGroup: g, taxPerUnit, tax: taxPerUnit * item.quantity };
            }
            return item;
        }));
        // Attach GST only when groups map arrives; avoid re-running on every cart mutation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gstGroupsMap]);

    // Ensure barcode input is focused whenever product modal closes (so consecutive scans work)
    useEffect(() => {
        if (!showProductModal) {
            // small timeout to allow modal close animation to finish
            setTimeout(() => barcodeInputRef.current?.focus(), 50);
        }
    }, [showProductModal]);

    // Always focus scan input when user clicks anywhere on the page while on this component
    // Skip focusing when the click target is an input/textarea or contentEditable element
    useEffect(() => {
        const handler = (e) => {
            try {
                const t = e.target;
                const tag = (t && t.tagName && t.tagName.toLowerCase()) || '';
                if (tag === 'input' || tag === 'textarea' || t.isContentEditable) return;
                // if looking up, still try focusing when lookup finishes
                // focus after current event handlers finish
                setTimeout(() => {
                    barcodeInputRef.current?.focus();
                }, 0);
            } catch (err) {
                // ignore errors
            }
        };

        document.addEventListener('click', handler, true);
        return () => document.removeEventListener('click', handler, true);
    }, []);

    // Show snackbar message
    // message: string; type: optional 'success'|'error'|'warning' to force style
    const showSnackBar = (message, type) => {
        setSnackBarMessage(message);

        // decide class based on type or message content
        let cls = 'bg-green-500 text-white'; // default success
        if (type === 'error') cls = 'bg-red-500 text-white';
        else if (type === 'warning') cls = 'bg-yellow-400 text-black';
        else if (typeof message === 'string') {
            const m = message.toLowerCase();
            // removed items -> warning (yellow)
            if (/\bremoved?\b|\bremove\b/.test(m)) {
                cls = 'bg-yellow-400 text-black';
            }
            // common error/validation patterns -> error (red)
            else if (/error|failed|please try again|please enter|scan|not found|failed to place|failed to/.test(m)) {
                cls = 'bg-red-500 text-white';
            }
        }

        setSnackBarClass(cls);
        setIsSnackBarVisible(true);
        setTimeout(() => setIsSnackBarVisible(false), 6000);

        // If message asks for barcode input, ensure scan input is focused
        try {
            if (typeof message === 'string' && /barcode|scan|please enter on screen a barcode/i.test(message)) {
                setTimeout(() => barcodeInputRef.current?.focus(), 50);
            }
        } catch (e) {
            // ignore
        }
    };

    // Handle barcode scan/input
    const handleBarcodeSubmit = async (e) => {
        e?.preventDefault();
        if (!barcode.trim()) {
            showSnackBar('Please enter or scan a barcode');
            return;
        }

        setIsLookingUp(true);
        try {
            const response = await lookupBarcode({ barcode: barcode.trim() });

            if (response.success && response.data) {
                const product = response.data;

                // If product has multiple options, show modal to select
                if (product.productOptions && product.productOptions.length > 1) {
                    setSelectedProduct(product);
                    setShowProductModal(true);
                } else {
                    // Add directly to cart with first option
                    addToCart(product, product.productOptions[0]);
                }

                setBarcode('');
            } else {
                showSnackBar(response.message || 'Product not found');
                // clear input so merchant can scan next item easily
                setBarcode('');
                setTimeout(() => barcodeInputRef.current?.focus(), 50);
            }
        } catch (error) {
            // Show server error message if available (e.g., product not enabled for offline store)
            const errorMessage = error?.errorData?.message || error?.message || 'Error looking up product. Please try again.';
            showSnackBar(errorMessage);
            // clear input when lookup fails due to network/server error
            setBarcode('');
            setTimeout(() => barcodeInputRef.current?.focus(), 50);
            console.error('Barcode lookup error:', error);
        } finally {
            setIsLookingUp(false);
            barcodeInputRef.current?.focus();
        }
    };

    // Add product to cart
    const addToCart = (product, selectedOption) => {
        // Validate stock before adding
        const availableQty = selectedOption.availableQuantity || 0;
        const isOutOfStock = product.isOutOfStock === true; // Check at PRODUCT level, not option level
        const isActive = product.isActive !== false;

        // Check if product can be added
        if (!isActive) {
            showSnackBar(`Cannot add ${product.productName} - Product is inactive`, 'error');
            setTimeout(() => barcodeInputRef.current?.focus(), 50);
            return;
        }

        if (isOutOfStock) {
            showSnackBar(`Cannot add ${product.productName} - Product is marked as out of stock`, 'error');
            setTimeout(() => barcodeInputRef.current?.focus(), 50);
            return;
        }

        if (availableQty <= 0) {
            showSnackBar(`Cannot add ${product.productName} - No stock available`, 'error');
            setTimeout(() => barcodeInputRef.current?.focus(), 50);
            return;
        }

        // For public API products, use barcode as temporary ID
        const tempId = product._id || product.barcode || `temp-${Date.now()}`;
        const tempProductId = product.productId || product.barcode || tempId;

        // determine gst group for the product/option
        const gstGroupId = product.gstGroupId || (selectedOption && selectedOption.gstGroupId) || null;
        const gstGroup = gstGroupId ? gstGroupsMap[gstGroupId] || null : null;

        const priceValue = selectedOption.discountPrice || selectedOption.originalPrice || 0;

        // compute tax per unit depending on inclusive/exclusive
        let taxPerUnit = 0;
        if (gstGroup && typeof gstGroup.percentage === 'number') {
            const pct = Number(gstGroup.percentage) || 0;
            if (gstGroup.type === 'exclusive') {
                taxPerUnit = (priceValue * pct) / 100;
            } else {
                // inclusive: extract tax portion from the price (price includes tax)
                taxPerUnit = (priceValue * pct) / (100 + pct);
            }
        }

        const cartItem = {
            _id: tempId,
            productId: tempProductId,
            productName: product.productName,
            image: product.images?.[0]?.preview || product.images?.[0]?.url || '',
            selectedOption: selectedOption,
            quantity: 1,
            price: priceValue,
            total: priceValue,
            gstGroup: gstGroup,
            gstGroupId: gstGroupId,
            taxPerUnit: taxPerUnit,
            tax: taxPerUnit * 1,
            source: product.source || 'internal', // Track if from public API
            availableQuantity: availableQty,
            isOutOfStock: product.isOutOfStock === true, // Store from PRODUCT level
            isActive: product.isActive !== false, // Store from PRODUCT level
            commonInventoryType: product.commonInventoryType === true, // Track if product uses common inventory
            categoryId: product.category || product.categoryId || null,
            subcategoryId: product.subcategoryId || product.subCategoryId || null,
        };

        setCart(prevCart => {
            const existingIndex = prevCart.findIndex(
                item => item._id === tempId && item.selectedOption._id === selectedOption._id
            );

            if (existingIndex >= 0) {
                const updated = [...prevCart];
                const existingItem = updated[existingIndex];

                // Check stock before incrementing for common inventory
                if (existingItem.commonInventoryType === true) {
                    const weightPerUnit = selectedOption.weight?.weightValue || 0;
                    const totalWeightAfterAdd = (existingItem.quantity + 1) * weightPerUnit;
                    if (totalWeightAfterAdd > availableQty) {
                        // Convert available quantity to the option's metric for display
                        let displayAvailable = availableQty;
                        let displayUnit = 'units';

                        if (selectedOption.weight?.metricType === 'supporting' && selectedOption.weight?.conversionFactor) {
                            // Convert from primary (Kg) to supporting (gm)
                            displayAvailable = availableQty / selectedOption.weight.conversionFactor;
                            displayUnit = selectedOption.weight.metricLabel || 'units';
                        } else if (selectedOption.weight?.metricType === 'primary') {
                            // Already in primary metric
                            displayUnit = selectedOption.weight.metricLabel || 'units';
                        }

                        const formattedAvailable = parseFloat(displayAvailable.toFixed(3));
                        showSnackBar(`Cannot add more - Only ${formattedAvailable} ${displayUnit} available`, 'error');
                        setTimeout(() => barcodeInputRef.current?.focus(), 50);
                        return prevCart;
                    }
                } else {
                    // For regular products, check quantity
                    if (existingItem.quantity + 1 > availableQty) {
                        showSnackBar(`Cannot add more - Only ${availableQty} units available`, 'error');
                        setTimeout(() => barcodeInputRef.current?.focus(), 50);
                        return prevCart;
                    }
                }

                updated[existingIndex].quantity += 1;
                updated[existingIndex].total = updated[existingIndex].quantity * updated[existingIndex].price;
                updated[existingIndex].tax = (updated[existingIndex].taxPerUnit || 0) * updated[existingIndex].quantity;
                return updated;
            }
            return [...prevCart, cartItem];
        });

        setShowProductModal(false);
        setSelectedProduct(null);
        setShowFindItemModal(false);
        // refocus barcode input so the merchant can scan the next item immediately
        setTimeout(() => barcodeInputRef.current?.focus(), 50);
        if (product.source === 'public_api' && cartItem.price === 0) {
            showSnackBar(`${product.productName} added. Please set price before checkout.`);
        } else {
            showSnackBar(`${product.productName} added to cart`);
        }
    };

    const handleFindItemSelect = (product) => {
        const opts = product?.productOptions || [];
        if (!opts.length) {
            showSnackBar(`No options available for ${product.productName}`, 'error');
            return;
        }
        if (opts.length > 1) {
            setSelectedProduct(product);
            setShowProductModal(true);
        } else {
            addToCart(product, opts[0]);
        }
    };

    // Validate if current coupon is still applicable based on cart contents
    const validateCouponEligibility = (updatedCart) => {
        if (!selectedCoupon) return;

        // Calculate subtotal of updated cart
        const subtotal = updatedCart.reduce((sum, item) => sum + (item.total || 0), 0);

        // Check minimum order value
        if (selectedCoupon.minOrderValue && subtotal < selectedCoupon.minOrderValue) {
            setSelectedCoupon(null);
            setCouponDiscount(0);
            showSnackBar(`Coupon removed: Minimum order value of ₹${formatIndianNumber(selectedCoupon.minOrderValue)} not met`, 'info');
            return;
        }

        // Check if coupon still applies to products in cart
        const couponProductIds = selectedCoupon.productIds || [];
        const hasValidProducts = couponProductIds.length === 0 ||
            updatedCart.some(item => couponProductIds.includes(item._id) || couponProductIds.includes(item.productId));

        if (!hasValidProducts) {
            setSelectedCoupon(null);
            setCouponDiscount(0);
            showSnackBar('Coupon removed: Not applicable to remaining items', 'info');
            return;
        }

        // Recalculate discount based on new subtotal
        let discount = 0;
        if (selectedCoupon.discountType === 'percentage') {
            discount = (subtotal * selectedCoupon.discountValue) / 100;
        } else {
            discount = selectedCoupon.discountValue;
        }

        // Apply max discount limit
        if (selectedCoupon.maxDiscountValue && discount > selectedCoupon.maxDiscountValue) {
            discount = selectedCoupon.maxDiscountValue;
        }

        setCouponDiscount(discount);
    };

    // Update cart item quantity
    const updateQuantity = (index, delta) => {
        setCart(prevCart => {
            const updated = [...prevCart];
            const item = updated[index];
            const newQuantity = item.quantity + delta;

            if (newQuantity <= 0) {
                const updatedCart = updated.filter((_, i) => i !== index);

                // If cart becomes empty, remove applied coupon
                if (updatedCart.length === 0 && selectedCoupon) {
                    setSelectedCoupon(null);
                    setCouponDiscount(0);
                } else {
                    // Validate coupon eligibility with updated cart
                    setTimeout(() => validateCouponEligibility(updatedCart), 0);
                }

                return updatedCart;
            }

            // Check if increasing quantity exceeds available stock
            if (delta > 0 && item.availableQuantity !== undefined) {
                if (item.commonInventoryType === true) {
                    // For common inventory, check total weight
                    const weightPerUnit = item.selectedOption?.weight?.weightValue || 0;
                    const totalWeight = newQuantity * weightPerUnit;
                    if (totalWeight > item.availableQuantity) {
                        // Convert available quantity to the option's metric for display
                        let displayAvailable = item.availableQuantity;
                        let displayUnit = 'units';

                        if (item.selectedOption?.weight?.metricType === 'supporting' && item.selectedOption?.weight?.conversionFactor) {
                            // Convert from primary (Kg) to supporting (gm)
                            displayAvailable = item.availableQuantity / item.selectedOption.weight.conversionFactor;
                            displayUnit = item.selectedOption.weight.metricLabel || 'units';
                        } else if (item.selectedOption?.weight?.metricType === 'primary') {
                            // Already in primary metric
                            displayUnit = item.selectedOption.weight.metricLabel || 'units';
                        }

                        const formattedAvailable = parseFloat(displayAvailable.toFixed(3));
                        showSnackBar(`Cannot add more - Only ${formattedAvailable} ${displayUnit} available`, 'error');
                        return prevCart;
                    }
                } else {
                    // For regular products, check quantity
                    if (newQuantity > item.availableQuantity) {
                        showSnackBar(`Cannot add more - Only ${item.availableQuantity} units available`, 'error');
                        return prevCart;
                    }
                }
            }

            updated[index].quantity = newQuantity;
            updated[index].total = updated[index].quantity * updated[index].price;
            updated[index].tax = (updated[index].taxPerUnit || 0) * updated[index].quantity;

            // Validate coupon eligibility with updated cart
            setTimeout(() => validateCouponEligibility(updated), 0);

            return updated;
        });

        // refocus barcode input so the user can continue scanning
        setTimeout(() => barcodeInputRef.current?.focus(), 50);
    };

    // Remove item from cart
    const removeFromCart = (index) => {
        setCart(prevCart => {
            const updatedCart = prevCart.filter((_, i) => i !== index);

            // If cart becomes empty, remove applied coupon
            if (updatedCart.length === 0 && selectedCoupon) {
                setSelectedCoupon(null);
                setCouponDiscount(0);
            } else {
                // Validate coupon eligibility with updated cart
                setTimeout(() => validateCouponEligibility(updatedCart), 0);
            }

            return updatedCart;
        });
        showSnackBar('Item removed from cart');
        // refocus barcode input after removing
        setTimeout(() => barcodeInputRef.current?.focus(), 50);
    };

    // Apply coupon to cart
    const applyCoupon = (coupon) => {
        const { subtotal } = calculateTotals();

        // Validate minimum order value
        if (coupon.minOrderValue && subtotal < coupon.minOrderValue) {
            showSnackBar(`Minimum order value of ₹${formatIndianNumber(coupon.minOrderValue)} required`, 'error');
            return;
        }

        // Check if coupon applies to products in cart
        const couponProductIds = coupon.productIds || [];
        const hasValidProducts = couponProductIds.length === 0 ||
            cart.some(item => couponProductIds.includes(item._id) || couponProductIds.includes(item.productId));

        if (!hasValidProducts) {
            showSnackBar('Coupon not applicable to items in cart', 'error');
            return;
        }

        // Calculate discount
        let discount = 0;
        if (coupon.discountType === 'percentage') {
            discount = (subtotal * coupon.discountValue) / 100;
        } else {
            discount = coupon.discountValue;
        }

        // Apply max discount limit
        if (coupon.maxDiscountValue && discount > coupon.maxDiscountValue) {
            discount = coupon.maxDiscountValue;
        }

        // Validate maximum total usage BEFORE applying coupon
        if (coupon.maxUsageTotal) {
            const currentUsage = coupon.usageCount || 0;
            const newTotalUsage = currentUsage + discount;

            if (newTotalUsage > coupon.maxUsageTotal) {
                const remainingUsage = coupon.maxUsageTotal - currentUsage;
                showSnackBar(
                    `Coupon usage limit exceeded! Maximum total usage: ₹${formatIndianNumber(coupon.maxUsageTotal)}. Already used: ₹${formatIndianNumber(currentUsage)}. Remaining: ₹${formatIndianNumber(remainingUsage)}. This order requires: ₹${formatIndianNumber(discount)}.`,
                    'error'
                );
                return;
            }
        }

        setSelectedCoupon(coupon);
        setCouponDiscount(discount);
        setShowCouponModal(false);
        showSnackBar(`Coupon "${coupon.couponCode}" applied! You save ₹${formatIndianNumber(discount)}`, 'success');
    };

    // Remove applied coupon
    const removeCoupon = () => {
        setSelectedCoupon(null);
        setCouponDiscount(0);
        showSnackBar('Coupon removed', 'info');
    };

    // Calculate cart totals
    const calculateTotals = () => {
        const subtotal = cart.reduce((sum, item) => sum + (item.total || 0), 0);

        // Sum tax only for exclusive products — inclusive products' tax is already included in price
        const exclusiveTax = cart.reduce((sum, item) => {
            const isExclusive = item.gstGroup && item.gstGroup.type === 'exclusive';
            return sum + ((isExclusive ? (item.tax || 0) : 0) || 0);
        }, 0);

        // Sum tax for inclusive products (tax portion embedded in price)
        const inclusiveTax = cart.reduce((sum, item) => {
            const isInclusive = item.gstGroup && item.gstGroup.type === 'inclusive';
            return sum + ((isInclusive ? (item.tax || 0) : 0) || 0);
        }, 0);

        // Calculate total after coupon + POS offer discount
        const totalBeforeCoupon = subtotal + exclusiveTax;
        const totalAfterCoupon = totalBeforeCoupon - couponDiscount - offerDiscount;
        const totalBeforeRoundOff = Math.max(0, totalAfterCoupon); // Ensure total doesn't go negative

        // Calculate round off
        const roundedTotal = Math.round(totalBeforeRoundOff);
        const roundOff = roundedTotal - totalBeforeRoundOff;
        const total = roundedTotal;

        return { subtotal, exclusiveTax, inclusiveTax, total, totalBeforeCoupon, totalBeforeRoundOff, roundOff };
    };

    // Check if any coupon's minimum order value is met
    const canApplyCoupon = () => {
        if (cart.length === 0 || availableCoupons.length === 0) return false;

        const { subtotal } = calculateTotals();

        // Check if at least one coupon's minimum order value is met
        return availableCoupons.some(coupon => {
            const minOrderValue = coupon.minOrderValue || 0;
            return subtotal >= minOrderValue;
        });
    };

    // Place walk-in order
    const buildOrderPayload = () => {
        const { subtotal, exclusiveTax, inclusiveTax, total, roundOff } = calculateTotals();
        const appliedWithBenefit = !!(
            posOfferEval.applied
            && (offerDiscount > 0 || (posOfferEval.applied.freeItems || []).length > 0)
        );
        // Classic (Behaviour OFF): never print loyalty progress/unlocked teases on the receipt.
        // Behaviour ON: allow unlocked / non-nudge progress (same as before).
        let teaseName = null;
        let teaseMessage = null;
        let teaseHighlight = null;
        if (!appliedWithBenefit && behaviourPaymentEnabled) {
            const unlocked = posOfferEval.unlocked?.[0];
            if (unlocked) {
                teaseName = unlocked.name || null;
                teaseMessage = unlocked.message || null;
                teaseHighlight = unlocked.name ? `★ ${unlocked.name} ★` : null;
            } else {
                const progress = (posOfferEval.progress || []).find(
                    (p) => p && p.offerId !== 'mobile-nudge' && !isEnterMobileLoyaltyNudge(p),
                );
                if (progress) {
                    teaseName = progress.name || null;
                    teaseMessage = progress.message || null;
                    teaseHighlight = progress.name ? `★ LOYALTY: ${progress.name} ★` : null;
                }
            }
        }
        return {
            orderType: 'walk-in',
            items: cart.map(item => {
                const base = {
                    productId: item._id,
                    productName: item.productName,
                    image: item.image,
                    hsnCode: item.hsnCode,
                    gstGroupId: item.gstGroupId,
                    selectedOption: item.selectedOption,
                    quantity: item.quantity,
                    price: item.price,
                    total: item.total,
                    gstGroup: item.gstGroup || null,
                    commonInventoryType: item.commonInventoryType || false,
                };
                return {
                    ...base,
                    tax: Number((item.tax || 0).toFixed ? (item.tax || 0) : (Number(item.tax) || 0)),
                    categoryId: item.categoryId || null,
                    subcategoryId: item.subcategoryId || null,
                    isFreeOfferItem: item.isFreeOfferItem === true,
                    freeCatalogPrice: Number(item.freeCatalogPrice) || 0,
                };
            }),
            financial: {
                subtotal,
                taxInclusive: Number((inclusiveTax || 0).toFixed ? (inclusiveTax || 0) : Number(inclusiveTax || 0)),
                taxExclusive: Number((exclusiveTax || 0).toFixed ? (exclusiveTax || 0) : Number(exclusiveTax || 0)),
                savedAmount: Number(((inclusiveTax || 0) - (exclusiveTax || 0)).toFixed(2)),
                couponDiscount: couponDiscount,
                couponCode: selectedCoupon?.couponCode || null,
                couponId: selectedCoupon?._id || null,
                offerDiscount: offerDiscount,
                offerId: appliedWithBenefit ? posOfferEval.applied.offerId : null,
                offerName: appliedWithBenefit
                    ? posOfferEval.applied.name
                    : teaseName,
                offerMessage: appliedWithBenefit
                    ? (posOfferEval.applied.message
                        || ((posOfferEval.applied.freeItemsWorth > 0)
                            ? `Items worth ₹${Number(posOfferEval.applied.freeItemsWorth).toFixed(0)} free`
                            : null))
                    : teaseMessage,
                offerBillHighlight: appliedWithBenefit
                    ? (posOfferEval.applied.billHighlight
                        || ((posOfferEval.applied.freeItemsWorth > 0)
                            ? `★ FREE GIFTS: ITEMS WORTH ₹${Number(posOfferEval.applied.freeItemsWorth).toFixed(0)} FREE ★`
                            : null))
                    : teaseHighlight,
                freeItemsWorth: Number(posOfferEval.applied?.freeItemsWorth || 0) || 0,
                roundOff: Number(roundOff.toFixed(2)),
                finalAmount: total
            },
            paymentMethod,
        };
    };

    const finalizeWalkInOrder = async (orderPayload) => {
        const mobileDigits = String(customerMobile).replace(/\D/g, '').slice(-10);
        const payload = {
            ...orderPayload,
            customer: mobileDigits.length === 10
                ? {
                    customerName: customerName.trim() || 'Walk-in Customer',
                    customerPhone: mobileDigits,
                }
                : undefined,
            posPaymentType: behaviourPaymentEnabled ? 'behaviour' : 'classic',
        };
        const response = await createWalkInOrder(payload);
        if (!response.success) {
            showSnackBar(response.message || 'Failed to place order', 'error');
            return false;
        }
        showSnackBar('Order placed! Sending to printer...', 'success');
        const printResult = await printToThermalPrinter(response.data.orderDetails);
        if (printResult.success) {
            showSnackBar(`✓ ${printResult.message}`, 'success');
        } else {
            showSnackBar('⚠ Order saved but print failed - ' + (printResult.error || 'Unknown error'), 'warning');
        }
        setCart([]);
        setSelectedCoupon(null);
        setCouponDiscount(0);
        setOfferDiscount(0);
        setPosOfferEval({ applied: null, progress: [], unlocked: [] });
        setPaymentMethod(null);
        setCustomerMobile('');
        setCustomerName('');
        setMobileSuggestions([]);
        setShowMobileSuggestions(false);
        setTimeout(() => barcodeInputRef.current?.focus(), 1500);
        return true;
    };

    const validateStockForCart = async () => {
        const validationPayload = {
            items: cart.map(item => ({
                productId: item._id,
                productName: item.productName,
                selectedOption: {
                    _id: item.selectedOption._id,
                    weight: item.selectedOption.weight
                },
                quantity: item.quantity,
                commonInventoryType: item.commonInventoryType || false
            }))
        };
        const validationResponse = await validateCartStock(validationPayload);
        if (!validationResponse.success || !validationResponse.isValid) {
            const invalidItems = validationResponse.invalidItems || [];
            if (invalidItems.length > 0) {
                let errorMsg = 'Cannot place order:\n\n';
                invalidItems.forEach(item => {
                    let weightDisplay = '';
                    if (item.optionWeight) {
                        if (typeof item.optionWeight === 'object') {
                            weightDisplay = item.optionWeight.weightLabel || `${item.optionWeight.weightValue} ${item.optionWeight.metricLabel}`;
                        } else {
                            weightDisplay = item.optionWeight;
                        }
                    }
                    if (item.reason === 'Out of stock') {
                        errorMsg += `• ${item.productName}${weightDisplay ? ` (${weightDisplay})` : ''} - Out of stock\n`;
                    } else if (item.reason === 'Insufficient stock') {
                        errorMsg += `• ${item.productName}${weightDisplay ? ` (${weightDisplay})` : ''} - Only ${item.availableQuantity} available (you have ${item.requestedQuantity} in cart)\n`;
                    } else if (item.reason === 'Product is inactive') {
                        errorMsg += `• ${item.productName} - Product is inactive\n`;
                    } else {
                        errorMsg += `• ${item.productName} - ${item.reason}\n`;
                    }
                });
                showSnackBar(errorMsg.trim(), 'error');
                return false;
            }
            showSnackBar('Stock validation failed. Please check your cart.', 'error');
            return false;
        }
        return true;
    };

    /** Behaviour Payment toggle — persists immediately. Locked when Customer Analytics Behaviour is ON. */
    const toggleBehaviourPayment = async (enabled) => {
        if (togglingBehaviour) return;
        if (behaviourForcedOn && !enabled) {
            showSnackBar(
                'Behaviour is locked ON from Customer Analytics — turn Behaviour OFF there to unlock',
                'error',
            );
            return;
        }
        const prev = behaviourPaymentEnabled;
        setBehaviourPaymentEnabled(enabled);
        if (!enabled) {
            setCustomerMobile('');
            setCustomerName('');
            setMobileSuggestions([]);
            setShowMobileSuggestions(false);
        }
        setTogglingBehaviour(true);
        try {
            const res = await pipUpdateConfig({ behaviourPaymentEnabled: enabled });
            if (res?.success) {
                const forced = res.data.behaviourForcedOn !== false;
                setBehaviourForcedOn(forced);
                setBehaviourPaymentEnabled(forced ? true : !!res.data.behaviourPaymentEnabled);
                setBehaviourRequireMobile(res.data.behaviourRequireMobile !== false);
                setBehaviourRequireCustomerName(res.data.behaviourRequireCustomerName !== false);
                if (forced && !enabled) {
                    showSnackBar(
                        'Behaviour is locked ON from Customer Analytics — turn Behaviour OFF there to unlock',
                        'error',
                    );
                } else {
                    showSnackBar(
                        res.data.behaviourPaymentEnabled ? 'Behaviour Payment ON' : 'Behaviour Payment OFF',
                        'success',
                    );
                }
            } else {
                setBehaviourPaymentEnabled(prev);
                showSnackBar(res?.message || 'Could not update Behaviour Payment', 'error');
            }
        } catch (e) {
            setBehaviourPaymentEnabled(prev);
            showSnackBar('Could not update Behaviour Payment', 'error');
        } finally {
            setTogglingBehaviour(false);
        }
    };

    const handlePlaceOrder = async () => {
        if (cart.length === 0) return;

        if (!paymentMethod) {
            showSnackBar('Please select a payment method (Cash, Card, or UPI)', 'error');
            return;
        }

        if (isMasterAccount || isWarehouse) {
            showSnackBar('Order placement not allowed for warehouse accounts. Please use a branch account.', 'error');
            return;
        }

        if (behaviourPaymentEnabled) {
            if (behaviourRequireMobile) {
                const mobile = String(customerMobile).replace(/\D/g, '');
                if (mobile.length !== 10) {
                    showSnackBar('Enter a valid 10-digit mobile number', 'error');
                    return;
                }
            }
            if (behaviourRequireCustomerName && !String(customerName || '').trim()) {
                showSnackBar('Enter customer name', 'error');
                return;
            }
        }

        setIsProcessingOrder(true);
        showSnackBar('Validating stock availability...', 'info');

        try {
            const ok = await validateStockForCart();
            if (!ok) {
                setIsProcessingOrder(false);
                return;
            }

            const orderPayload = buildOrderPayload();
            showSnackBar('Stock validated. Processing order...', 'info');
            await finalizeWalkInOrder(orderPayload);
        } catch (error) {
            showSnackBar('Error placing order. Please try again.');
            console.error('Order placement error:', error);
        } finally {
            setIsProcessingOrder(false);
        }
    };

    const resetReturnSelection = useCallback((bill) => {
        setSelectedReturnBill(bill || null);
        const qtyMap = {};
        (bill?.items || []).forEach((item) => {
            if (item.returnableQuantity > 0) qtyMap[item.itemIndex] = 0;
        });
        setReturnQtyByIndex(qtyMap);
        setReturnReason('');
        setReturnNote('');
    }, []);

    const fetchRecentReturnBills = useCallback(async ({ page = 1, append = false, startTime, endTime } = {}) => {
        setRecentReturnBillsLoading(true);
        try {
            const payload = { page, limit: 10 };
            const st = startTime !== undefined ? startTime : appliedReturnStartTime;
            const et = endTime !== undefined ? endTime : appliedReturnEndTime;
            if (st && et) {
                payload.startTime = st;
                payload.endTime = et;
            }
            const res = await listWalkInOrdersForReturn(payload);
            if (res?.success && res.data) {
                const orders = Array.isArray(res.data.orders) ? res.data.orders : [];
                setRecentReturnBills((prev) => (append ? [...prev, ...orders] : orders));
                setRecentReturnLookback(res.data.lookback || 'today');
                setRecentReturnLookbackLabel(res.data.lookbackLabel || 'Today');
                setRecentReturnPagination(res.data.pagination || {
                    currentPage: page,
                    totalPages: 1,
                    totalCount: orders.length,
                    hasNextPage: false,
                    hasPrevPage: false,
                });
                // Clear search hits when refreshing the browse list
                if (!append) setReturnBills([]);
            } else {
                if (!append) setRecentReturnBills([]);
                showSnackBar(res?.message || 'Could not load recent bills', 'error');
            }
        } catch (err) {
            if (!append) setRecentReturnBills([]);
            showSnackBar(err?.message || 'Could not load recent bills', 'error');
        } finally {
            setRecentReturnBillsLoading(false);
        }
    }, [appliedReturnStartTime, appliedReturnEndTime]);

    useEffect(() => {
        if (posWorkspaceMode !== 'returns') return undefined;
        fetchRecentReturnBills({ page: 1, append: false });
        return undefined;
    }, [posWorkspaceMode, fetchRecentReturnBills]);

    const handleReturnLookup = async (e) => {
        e?.preventDefault?.();
        const q = String(returnSearch || '').trim();
        if (!q) {
            showSnackBar('Enter invoice number or mobile number', 'error');
            return;
        }
        setReturnLookupLoading(true);
        try {
            const res = await lookupWalkInOrder({ searchTerm: q, limit: 20 });
            if (res?.success) {
                const list = Array.isArray(res.data) ? res.data : [];
                setReturnBills(list);
                if (list.length === 1) resetReturnSelection(list[0]);
                else resetReturnSelection(null);
                if (!list.length) showSnackBar('No matching walk-in bills found', 'info');
            } else {
                setReturnBills([]);
                resetReturnSelection(null);
                showSnackBar(res?.message || 'Lookup failed', 'error');
            }
        } catch (err) {
            setReturnBills([]);
            resetReturnSelection(null);
            showSnackBar(err?.message || 'Lookup failed', 'error');
        } finally {
            setReturnLookupLoading(false);
        }
    };

    const returnSelectionTotal = (() => {
        if (!selectedReturnBill) return { qty: 0, refund: 0 };
        let qty = 0;
        let refund = 0;
        (selectedReturnBill.items || []).forEach((item) => {
            const q = Math.max(0, Math.floor(Number(returnQtyByIndex[item.itemIndex]) || 0));
            if (q <= 0) return;
            qty += q;
            refund += (Number(item.unitPrice) || 0) * q;
        });
        return { qty, refund: Math.round(refund * 100) / 100 };
    })();

    const handleConfirmReturn = async () => {
        if (!selectedReturnBill?._id) {
            showSnackBar('Select a bill first', 'error');
            return;
        }
        if (returnReason !== 'just_return' && returnReason !== 'damaged') {
            showSnackBar('Select Damaged product or Just return', 'error');
            return;
        }
        const items = (selectedReturnBill.items || [])
            .map((item) => ({
                itemIndex: item.itemIndex,
                quantity: Math.max(0, Math.floor(Number(returnQtyByIndex[item.itemIndex]) || 0)),
            }))
            .filter((row) => row.quantity > 0);
        if (!items.length) {
            showSnackBar('Select quantity for at least one item', 'error');
            return;
        }
        setReturnSubmitting(true);
        try {
            const res = await returnWalkInOrderItems({
                orderId: selectedReturnBill._id,
                items,
                reason: returnReason,
                note: returnNote,
            });
            if (res?.success) {
                showSnackBar(res.message || 'Return recorded', 'success');
                const updated = res.data?.order;
                if (updated) {
                    setReturnBills((prev) => prev.map((b) => (String(b._id) === String(updated._id) ? updated : b)));
                    setRecentReturnBills((prev) => prev.map((b) => (String(b._id) === String(updated._id) ? updated : b)));
                } else {
                    await handleReturnLookup();
                }
                // Close return modal after successful confirm
                resetReturnSelection(null);
                fetchRecentReturnBills({ page: 1, append: false });
            } else {
                showSnackBar(res?.message || 'Return failed', 'error');
            }
        } catch (err) {
            showSnackBar(err?.message || 'Return failed', 'error');
        } finally {
            setReturnSubmitting(false);
        }
    };

    const { subtotal, exclusiveTax, inclusiveTax, total, roundOff } = calculateTotals();

    const hasFreeGiftApply = !!(posOfferEval.applied?.freeItems?.length);
    const hasCashOfferApply = offerDiscount > 0 || (posOfferEval.applied && Number(posOfferEval.applied.discount) > 0);
    // Classic POS (Behaviour OFF): hide mobile-nudge loyalty teases on screen and receipt.
    // Keep real progress when Behaviour is ON (or when progress is not a mobile nag).
    const visibleOfferProgress = (posOfferEval.progress || []).filter((p) => {
        if (!behaviourPaymentEnabled && isEnterMobileLoyaltyNudge(p)) return false;
        return true;
    });
    // Unlocked teases are Behaviour-mode loyalty UX; Classic only shows applied rewards.
    const visibleUnlocked = behaviourPaymentEnabled ? (posOfferEval.unlocked || []) : [];
    const showCouponLoyaltyConflict = !!(
        selectedCoupon && posOfferEval.applied && !posOfferEval.applied.stackWithCoupon && offerDiscount === 0
    );
    const loyaltyOfferBanners = (
        hasCashOfferApply
        || hasFreeGiftApply
        || visibleUnlocked.length > 0
        || visibleOfferProgress.length > 0
        || showCouponLoyaltyConflict
    ) ? (
        <div className="space-y-2 mb-4">
            {hasCashOfferApply && posOfferEval.applied && (
                <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3">
                    <div className="flex items-start gap-2">
                        <FaGift className="text-amber-600 mt-0.5 shrink-0" size={18} />
                        <div className="min-w-0">
                            <p className="font-bold text-amber-900 text-sm">
                                {posOfferEval.applied.billHighlight || posOfferEval.applied.name}
                            </p>
                            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                                {posOfferEval.applied.message} <span className="text-sm font-semibold text-amber-700 ml-2"> ₹{formatIndianNumber(offerDiscount || posOfferEval.applied.discount || 0)} OFF</span>    
                            </p>
                        </div>
                    </div>
                </div>
            )}
            {hasFreeGiftApply && posOfferEval.applied && (
                <div className="rounded-lg border-2 border-emerald-400 bg-emerald-50 p-3">
                    <div className="flex items-start gap-2">
                        <FaGift className="text-emerald-600 mt-0.5 shrink-0" size={18} />
                        <div className="min-w-0">
                            <p className="font-bold text-emerald-900 text-sm">
                                {posOfferEval.applied.billHighlight
                                    || `★ FREE GIFTS: ITEMS WORTH ₹${Number(posOfferEval.applied.freeItemsWorth || 0).toFixed(0)} FREE ★`}
                            </p>
                            <p className="text-xs text-emerald-800 mt-1 leading-relaxed">
                                {posOfferEval.applied.message
                                    || `Items worth ₹${Number(posOfferEval.applied.freeItemsWorth || 0).toFixed(0)} free — added to this bill at ₹0.`}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            {!hasCashOfferApply && !hasFreeGiftApply
                && visibleUnlocked.slice(0, 1).map((u) => (
                <div key={`u-${u.offerId}`} className="rounded-lg border border-teal-600 bg-teal-50 p-3">
                    <div className="flex items-start gap-2">
                        <FaGift className="text-teal-600 mt-0.5 shrink-0" size={16} />
                        <p className="text-xs text-teal-900 leading-relaxed font-medium">{u.message}</p>
                    </div>
                </div>
            ))}
            {!hasCashOfferApply && !hasFreeGiftApply
                && !(visibleUnlocked.length)
                && visibleOfferProgress.slice(0, 1).map((p) => (
                <div key={`p-${p.offerId}`} className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                    <div className="flex items-start gap-2">
                        <IoInformationCircle className="text-yellow-500 mt-0.5 shrink-0" size={20} />
                        <p className="text-sm text-yellow-800 leading-relaxed">{p.message}</p>
                    </div>
                </div>
            ))}
            {showCouponLoyaltyConflict && (
                <p className="text-[11px] text-amber-700">
                    This loyalty offer cannot stack with a coupon — remove coupon to apply it.
                </p>
            )}
        </div>
    ) : null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
            {/* Snackbar */}
            <AnimatePresence>
                {isSnackBarVisible && (
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        className={`fixed top-4 right-4 ${snackBarClass} px-6 py-3 rounded-lg shadow-lg z-50`}
                    >
                        {snackBarMessage}
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="max-w-7xl mx-auto">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <h1 className="text-lg font-bold text-slate-800 tracking-tight leading-none">
                        Merchant Billing
                    </h1>
                    <div
                        role="tablist"
                        aria-label="Workspace"
                        className="inline-flex h-8 items-center rounded-full bg-slate-900/5 p-0.5 ring-1 ring-slate-200/80"
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={posWorkspaceMode === 'billing'}
                            onClick={() => setPosWorkspaceMode('billing')}
                            className={`h-7 px-3.5 rounded-full text-[11px] font-semibold tracking-wide uppercase transition-all ${
                                posWorkspaceMode === 'billing'
                                    ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200/70'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            Billing
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={posWorkspaceMode === 'returns'}
                            onClick={() => setPosWorkspaceMode('returns')}
                            className={`h-7 px-3.5 rounded-full text-[11px] font-semibold tracking-wide uppercase transition-all inline-flex items-center gap-1 ${
                                posWorkspaceMode === 'returns'
                                    ? 'bg-white text-rose-700 shadow-sm ring-1 ring-slate-200/70'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            <FaUndo className="text-[9px] opacity-80" />
                            Returns
                        </button>
                    </div>
                </div>

                {posWorkspaceMode === 'returns' && (
                    <MerchantBillingReturnsPanel
                        returnSearch={returnSearch}
                        setReturnSearch={setReturnSearch}
                        returnLookupLoading={returnLookupLoading}
                        handleReturnLookup={handleReturnLookup}
                        returnBills={returnBills}
                        selectedReturnBill={selectedReturnBill}
                        resetReturnSelection={resetReturnSelection}
                        returnQtyByIndex={returnQtyByIndex}
                        setReturnQtyByIndex={setReturnQtyByIndex}
                        returnReason={returnReason}
                        setReturnReason={setReturnReason}
                        returnNote={returnNote}
                        setReturnNote={setReturnNote}
                        returnSelectionTotal={returnSelectionTotal}
                        handleConfirmReturn={handleConfirmReturn}
                        returnSubmitting={returnSubmitting}
                        recentBills={recentReturnBills}
                        recentBillsLoading={recentReturnBillsLoading}
                        recentBillsLookbackLabel={recentReturnLookbackLabel}
                        recentBillsPagination={recentReturnPagination}
                        onRefreshRecentBills={() => {
                            setReturnBills([]);
                            resetReturnSelection(null);
                            fetchRecentReturnBills({ page: 1, append: false });
                        }}
                        onRecentBillsPageChange={(page) => {
                            const nextPage = Math.max(1, Number(page) || 1);
                            resetReturnSelection(null);
                            fetchRecentReturnBills({ page: nextPage, append: false });
                        }}
                        returnStartTime={returnStartTime}
                        setReturnStartTime={setReturnStartTime}
                        returnEndTime={returnEndTime}
                        setReturnEndTime={setReturnEndTime}
                        onApplyReturnTimeFilter={() => {
                            if (!returnStartTime || !returnEndTime) {
                                showSnackBar('Select both start and end time', 'error');
                                return;
                            }
                            setAppliedReturnStartTime(returnStartTime);
                            setAppliedReturnEndTime(returnEndTime);
                        }}
                        onClearReturnTimeFilter={() => {
                            setReturnStartTime('');
                            setReturnEndTime('');
                            setAppliedReturnStartTime('');
                            setAppliedReturnEndTime('');
                        }}
                        showTodayTimeFilter={recentReturnLookback === 'today'}
                    />
                )}

                {posWorkspaceMode === 'billing' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Section - Barcode Scanner & Cart */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Barcode Scanner Card */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                                <h2 className="text-2xl font-semibold text-gray-700 flex items-center gap-2">
                                    <FaBarcode className="text-indigo-600" />
                                    Scan Product
                                </h2>
                                <button
                                    type="button"
                                    onClick={openFindItemModal}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 shadow-md hover:shadow-lg transition-all"
                                >
                                    <FaSearch className="text-sm" />
                                    Find Item
                                </button>
                            </div>

                            <form onSubmit={handleBarcodeSubmit} className="space-y-4">
                                <div className="relative">
                                    <input
                                        ref={barcodeInputRef}
                                        type="text"
                                        value={barcode}
                                        onChange={(e) => setBarcode(e.target.value)}
                                        placeholder="Scan barcode or enter manually"
                                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 text-lg"
                                        disabled={isLookingUp}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => barcodeInputRef.current?.focus()}
                                        aria-label="Focus scan input"
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                                    >
                                        <FaSearch />
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLookingUp}
                                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLookingUp ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                            Looking up...
                                        </span>
                                    ) : (
                                        'Add to Cart'
                                    )}
                                </button>
                            </form>
                        </div>

                        {loyaltyOfferBanners}

                        {/* Cart Items */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                <FaShoppingCart className="text-indigo-600" />
                                Cart Items ({cart.length})
                            </h2>

                            {cart.length === 0 ? (
                                <div className="text-center py-12">
                                    <FaShoppingCart className="text-6xl text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg">Cart is empty. Scan products to add.</p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
                                    {cart.map((item, index) => (
                                        <motion.div
                                            key={`${item._id}-${item.selectedOption._id}-${index}`}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                            className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                                        >
                                            {/* Product Image */}
                                            <img
                                                src={item.image || NO_IMAGE}
                                                alt={item.productName}
                                                onError={handleProductImageError}
                                                className="w-16 h-16 object-cover rounded-lg"
                                            />

                                            {/* Product Details */}
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-800 flex items-center gap-2 flex-wrap">
                                                    {item.productName}
                                                    {item.isFreeOfferItem && (
                                                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                                                          Free gift
                                                        </span>
                                                    )}
                                                </h3>
                                                <p className="text-sm text-gray-500">
                                                    {item.selectedOption.weight?.weightLabel || item.selectedOption.weightLabel || item.freeOptionLabel || 'N/A'}
                                                    {' - '}
                                                    {item.isFreeOfferItem ? (
                                                        <span className="text-emerald-700 font-semibold">
                                                          ₹0
                                                          {item.freeCatalogPrice > 0
                                                            ? ` (worth ₹${formatIndianNumber(item.freeCatalogPrice)})`
                                                            : ''}
                                                        </span>
                                                    ) : (
                                                        <>₹{formatIndianNumber(item.price)}</>
                                                    )}
                                                </p>
                                                {item.gstGroup && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        <span className={`px-2 py-0.5 rounded-full mr-2 ${item.gstGroup.type === 'exclusive' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                                            {item.gstGroup.type === 'exclusive' ? 'Exclusive' : 'Inclusive'}
                                                        </span>
                                                        <span className="font-medium">₹{formatIndianNumber(item.tax || 0)}</span>
                                                    </p>
                                                )}
                                                {/* Stock validation messages */}
                                                {(item.isActive === false || item.isOutOfStock === true || (item.availableQuantity !== undefined && item.availableQuantity <= 0)) && (
                                                    <div className="mt-2">
                                                        {item.isActive === false && (
                                                            <p className="text-xs text-red-600 font-semibold flex items-center gap-1">
                                                                <span className="inline-block w-2 h-2 bg-red-600 rounded-full"></span>
                                                                Product is inactive
                                                            </p>
                                                        )}
                                                        {item.isOutOfStock === true && (
                                                            <p className="text-xs text-red-600 font-semibold flex items-center gap-1">
                                                                <span className="inline-block w-2 h-2 bg-red-600 rounded-full"></span>
                                                                Out of stock
                                                            </p>
                                                        )}
                                                        {item.availableQuantity !== undefined && item.availableQuantity <= 0 && (
                                                            <p className="text-xs text-red-600 font-semibold flex items-center gap-1">
                                                                <span className="inline-block w-2 h-2 bg-red-600 rounded-full"></span>
                                                                No stock available
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                                {/* Warning when quantity exceeds available stock */}
                                                {item.availableQuantity !== undefined && item.availableQuantity > 0 && (() => {
                                                    if (item.commonInventoryType === true) {
                                                        const weightPerUnit = item.selectedOption?.weight?.weightValue || 0;
                                                        const totalWeight = item.quantity * weightPerUnit;

                                                        // Convert available quantity to the option's metric for display
                                                        let displayAvailable = item.availableQuantity;
                                                        let displayUnit = 'units';

                                                        if (item.selectedOption?.weight?.metricType === 'supporting' && item.selectedOption?.weight?.conversionFactor) {
                                                            // Convert from primary (Kg) to supporting (gm)
                                                            displayAvailable = item.availableQuantity / item.selectedOption.weight.conversionFactor;
                                                            displayUnit = item.selectedOption.weight.metricLabel || 'units';
                                                        } else if (item.selectedOption?.weight?.metricType === 'primary') {
                                                            // Already in primary metric
                                                            displayUnit = item.selectedOption.weight.metricLabel || 'units';
                                                        }

                                                        const formattedAvailable = parseFloat(displayAvailable.toFixed(3));
                                                        return totalWeight > item.availableQuantity ? (
                                                            <p className="text-xs text-orange-600 font-semibold mt-2 flex items-center gap-1">
                                                                <span className="inline-block w-2 h-2 bg-orange-600 rounded-full"></span>
                                                                Only {formattedAvailable} {displayUnit} available
                                                            </p>
                                                        ) : null;
                                                    } else {
                                                        return item.quantity > item.availableQuantity ? (
                                                            <p className="text-xs text-orange-600 font-semibold mt-2 flex items-center gap-1">
                                                                <span className="inline-block w-2 h-2 bg-orange-600 rounded-full"></span>
                                                                Only {item.availableQuantity} units available
                                                            </p>
                                                        ) : null;
                                                    }
                                                })()}
                                            </div>

                                            {/* Quantity Controls */}
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => updateQuantity(index, -1)}
                                                    className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition"
                                                >
                                                    <FaMinus size={12} />
                                                </button>
                                                <span className="w-8 text-center font-semibold">{item.quantity}</span>
                                                <button
                                                    onClick={() => updateQuantity(index, 1)}
                                                    className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition"
                                                >
                                                    <FaPlus size={12} />
                                                </button>
                                            </div>

                                            {/* Total Price */}
                                            <div className="text-right">
                                                <p className="font-bold text-gray-800">₹{formatIndianNumber(item.total)}</p>
                                            </div>

                                            {/* Remove Button */}
                                            <button
                                                onClick={() => removeFromCart(index)}
                                                className="p-2 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition"
                                            >
                                                <FaTrash />
                                            </button>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Section - Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
                            <h2 className="text-2xl font-semibold text-gray-700 mb-6">Order Summary</h2>

                            <div className="space-y-4">
                                <div className="flex justify-between text-gray-600">
                                    <span>Subtotal:</span>
                                    <span className="font-semibold">₹{formatIndianNumber(subtotal)}</span>
                                </div>

                                {inclusiveTax > 0 && (
                                    <div className="flex justify-between text-gray-600">
                                        <span>Tax (Inclusive):</span>
                                        <span className="font-semibold">₹{formatIndianNumber(inclusiveTax)}</span>
                                    </div>
                                )}

                                {exclusiveTax > 0 && (
                                    <div className="flex justify-between text-gray-600">
                                        <span>Tax (Exclusive):</span>
                                        <span className="font-semibold">₹{formatIndianNumber(exclusiveTax)}</span>
                                    </div>
                                )}

                                {/* Saved banner will be shown above the divider */}

                                {/* Saved amount/banner - compute savedValue */}
                                {(() => {
                                    const savedValue = (inclusiveTax || 0) - (exclusiveTax || 0);
                                    const savedAbs = Math.abs(Number(savedValue || 0));
                                    if (savedValue > 0) {
                                        return (
                                            <div className="mb-4">
                                                <div className="flex items-center justify-center gap-3 bg-green-50 p-2 rounded-md">
                                                    <div className="bg-green-600 text-white rounded-full p-1.5">
                                                        <FaCheckCircle />
                                                    </div>
                                                    <div className="text-sm text-green-800 font-semibold">
                                                        Hurray! You saved ₹{formatIndianNumber(savedAbs)} on this Order.
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }

                                    // If not a saving (exclusive >= inclusive), still show magnitude above divider without a negative sign
                                    if (savedAbs > 0) {
                                        return (
                                            <div className="flex justify-between text-gray-600 mb-4">
                                                <span>Inclusive - Exclusive (Saved in this order):</span>
                                                <span className="font-semibold">₹{formatIndianNumber(savedAbs)}</span>
                                            </div>
                                        );
                                    }

                                    return null;
                                })()}

                                {/* Coupon Application */}
                                <div className="border-t border-gray-200 pt-4 mt-4">
                                    {!selectedCoupon ? (
                                        <>
                                            <button
                                                onClick={() => setShowCouponModal(true)}
                                                disabled={!canApplyCoupon()}
                                                className="w-full py-3 px-4 border-2 border-dashed border-indigo-300 rounded-lg text-indigo-600 font-semibold hover:bg-indigo-50 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
                                            >
                                                Apply Coupon Code
                                            </button>
                                            {!canApplyCoupon() && cart.length > 0 && availableCoupons.length > 0 && (() => {
                                                const { subtotal } = calculateTotals();
                                                const unavailableCoupons = availableCoupons.filter(coupon => {
                                                    const minOrderValue = coupon.minOrderValue || 0;
                                                    if (subtotal >= minOrderValue) return false;

                                                    // Check if coupon has available usage left
                                                    if (coupon.maxUsageTotal) {
                                                        const currentUsage = coupon.usageCount || 0;
                                                        const remainingUsage = coupon.maxUsageTotal - currentUsage;

                                                        // Calculate potential discount
                                                        let potentialDiscount = 0;
                                                        if (coupon.discountType === 'percentage') {
                                                            potentialDiscount = (coupon.minOrderValue * coupon.discountValue) / 100;
                                                        } else {
                                                            potentialDiscount = coupon.discountValue;
                                                        }

                                                        // Apply max discount limit if exists
                                                        if (coupon.maxDiscountValue && potentialDiscount > coupon.maxDiscountValue) {
                                                            potentialDiscount = coupon.maxDiscountValue;
                                                        }

                                                        // Don't show coupon if it doesn't have enough remaining usage
                                                        if (remainingUsage < potentialDiscount) {
                                                            return false;
                                                        }
                                                    }

                                                    return true;
                                                });

                                                if (unavailableCoupons.length === 0) return null;

                                                return (
                                                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                                        <div className="flex items-start gap-2">
                                                            <IoInformationCircle className="text-blue-500 mt-0.5 flex-shrink-0" size={20} />
                                                            <div className="text-sm text-blue-700">
                                                                <p className="font-semibold mb-2">Add more items to unlock coupons:</p>
                                                                <div className="space-y-1.5">
                                                                    {unavailableCoupons.map((coupon, index) => {
                                                                        const needed = coupon.minOrderValue - subtotal;
                                                                        return (
                                                                            <div key={coupon._id || index} className="text-xs">
                                                                                <span className="font-medium">{coupon.couponCode}:</span> Add ₹{formatIndianNumber(needed)} more to avail{' '}
                                                                                {coupon.discountType === 'percentage'
                                                                                    ? `${coupon.discountValue}% OFF`
                                                                                    : `₹${formatIndianNumber(coupon.discountValue)} OFF`}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </>
                                    ) : (
                                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <p className="font-bold text-green-800">{selectedCoupon.couponCode}</p>
                                                    <p className="text-sm text-green-600">-₹{formatIndianNumber(couponDiscount)}</p>
                                                </div>
                                                <button
                                                    onClick={removeCoupon}
                                                    className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition"
                                                >
                                                    <IoClose size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {couponDiscount > 0 && (
                                    <div className="flex justify-between text-green-600 font-semibold">
                                        <span>Coupon Discount:</span>
                                        <span>-₹{formatIndianNumber(couponDiscount)}</span>
                                    </div>
                                )}

                                {offerDiscount > 0 && (
                                    <div className="flex justify-between text-amber-700 font-semibold">
                                        <span>Loyalty Offer:</span>
                                        <span>-₹{formatIndianNumber(offerDiscount)}</span>
                                    </div>
                                )}

                                {roundOff !== 0 && (
                                    <div className="flex justify-between text-gray-600">
                                        <span>Round Off:</span>
                                        <span className={roundOff > 0 ? 'text-green-600' : 'text-red-600'}>
                                            {roundOff > 0 ? '+' : ''}₹{formatIndianNumber(Math.abs(roundOff))}
                                        </span>
                                    </div>
                                )}

                                <div className="border-t-2 border-gray-200 pt-4">
                                    <div className="flex justify-between text-xl font-bold text-gray-800">
                                        <span>Total:</span>
                                        <span className="text-indigo-600">₹{formatIndianNumber(total)}</span>
                                    </div>
                                </div>

                                {/* Payment Method Selection */}
                                <div className="border-t border-gray-200 pt-4 mt-4">
                                    <div className="flex items-center justify-between mb-3 gap-2">
                                        <h3 className="text-sm font-semibold text-gray-700">Select Payment Method *</h3>
                                        <label
                                            className={`inline-flex items-center gap-2 select-none ${
                                                behaviourForcedOn ? 'cursor-not-allowed' : 'cursor-pointer'
                                            }`}
                                            title={
                                                behaviourForcedOn
                                                    ? 'Locked ON from Customer Analytics — turn Behaviour OFF there to unlock'
                                                    : 'Behaviour Payment — field rules come from Customer Analytics'
                                            }
                                        >
                                            <span className={`text-[11px] font-semibold ${behaviourPaymentEnabled ? 'text-indigo-700' : 'text-gray-500'}`}>
                                                Behaviour
                                                {behaviourForcedOn ? (
                                                    <span className="ml-1 text-[9px] font-bold uppercase tracking-wide text-indigo-500">
                                                        Locked
                                                    </span>
                                                ) : null}
                                            </span>
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={behaviourPaymentEnabled}
                                                disabled={togglingBehaviour || behaviourForcedOn}
                                                onClick={() => toggleBehaviourPayment(!behaviourPaymentEnabled)}
                                                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-70 ${
                                                    behaviourPaymentEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                                                }`}
                                            >
                                                <span
                                                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                                        behaviourPaymentEnabled ? 'translate-x-[18px]' : 'translate-x-[3px]'
                                                    }`}
                                                />
                                            </button>
                                        </label>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('Cash')}
                                            className={`py-3 px-4 rounded-lg font-semibold transition-all border-2 ${paymentMethod === 'Cash'
                                                    ? 'bg-green-500 text-white border-green-600 shadow-lg scale-105'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:bg-green-50'
                                                }`}
                                        >
                                            <div className="flex flex-col items-center">
                                                <FaMoneyBillWave className="text-2xl mb-1" />
                                                <span className="text-sm">Cash</span>
                                            </div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('Card')}
                                            className={`py-3 px-4 rounded-lg font-semibold transition-all border-2 ${paymentMethod === 'Card'
                                                    ? 'bg-blue-500 text-white border-blue-600 shadow-lg scale-105'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                                                }`}
                                        >
                                            <div className="flex flex-col items-center">
                                                <FaCreditCard className="text-2xl mb-1" />
                                                <span className="text-sm">Card</span>
                                            </div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('UPI')}
                                            className={`py-3 px-4 rounded-lg font-semibold transition-all border-2 ${paymentMethod === 'UPI'
                                                    ? 'bg-purple-500 text-white border-purple-600 shadow-lg scale-105'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                                                }`}
                                        >
                                            <div className="flex flex-col items-center">
                                                <FaMobileAlt className="text-2xl mb-1" />
                                                <span className="text-sm">UPI</span>
                                            </div>
                                        </button>
                                    </div>

                                    {behaviourPaymentEnabled && (
                                        <div className="mt-4 space-y-2">
                                            <div className="relative">
                                                <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                    Mobile number{behaviourRequireMobile ? ' *' : ''}
                                                </label>
                                                <input
                                                    type="tel"
                                                    inputMode="numeric"
                                                    maxLength={10}
                                                    value={customerMobile}
                                                    onChange={(e) => {
                                                        setCustomerMobile(e.target.value.replace(/\D/g, '').slice(0, 10));
                                                        setShowMobileSuggestions(true);
                                                    }}
                                                    onFocus={() => {
                                                        if (mobileSuggestions.length > 0) setShowMobileSuggestions(true);
                                                    }}
                                                    onBlur={() => {
                                                        setTimeout(() => setShowMobileSuggestions(false), 180);
                                                    }}
                                                    placeholder="10-digit mobile"
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                                                    autoComplete="off"
                                                />
                                                {loadingMobileSuggestions && (
                                                    <span className="absolute right-3 bottom-2.5 text-[10px] text-slate-400">…</span>
                                                )}
                                                {showMobileSuggestions && mobileSuggestions.length > 0 && (
                                                    <ul className="absolute left-0 right-0 top-full mt-1 z-20 max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg divide-y divide-slate-100">
                                                        {mobileSuggestions.map((s) => {
                                                            const name = String(s.customerName || 'Walk-in').trim();
                                                            const shortName = name.length > 18 ? `${name.slice(0, 18)}…` : name;
                                                            return (
                                                                <li key={s.mobile}>
                                                                    <button
                                                                        type="button"
                                                                        className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 transition-colors"
                                                                        onMouseDown={(e) => e.preventDefault()}
                                                                        onClick={() => {
                                                                            setCustomerMobile(String(s.mobile).replace(/\D/g, '').slice(0, 10));
                                                                            if (s.customerName) setCustomerName(s.customerName);
                                                                            setMobileSuggestions([]);
                                                                            setShowMobileSuggestions(false);
                                                                        }}
                                                                    >
                                                                        <div className="flex items-center gap-2 min-w-0 text-[13px] leading-snug">
                                                                            <span className="font-semibold text-slate-800 truncate max-w-[42%] shrink" title={name}>
                                                                                {shortName}
                                                                            </span>
                                                                            <span className="text-slate-300 shrink-0">·</span>
                                                                            <span className="tabular-nums font-semibold text-indigo-700 shrink-0">
                                                                                {s.mobile}
                                                                            </span>
                                                                            <span className="text-slate-300 shrink-0">·</span>
                                                                            <span className="text-slate-500 text-[12px] shrink-0 whitespace-nowrap">
                                                                                {s.orderCount || 1} orders
                                                                            </span>
                                                                        </div>
                                                                    </button>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                    Customer name{behaviourRequireCustomerName ? ' *' : ''}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={customerName}
                                                    onChange={(e) => setCustomerName(e.target.value)}
                                                    placeholder="Name"
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {!paymentMethod && cart.length > 0 && (
                                        <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                                            <span className="inline-block w-2 h-2 bg-red-500 rounded-full"></span>
                                            Please select a payment method to continue
                                        </p>
                                    )}
                                </div>

                                <button
                                    onClick={handlePlaceOrder}
                                    disabled={
                                        cart.length === 0
                                        || isProcessingOrder
                                        || !paymentMethod
                                        || (behaviourPaymentEnabled && behaviourRequireMobile && String(customerMobile).replace(/\D/g, '').length !== 10)
                                        || (behaviourPaymentEnabled && behaviourRequireCustomerName && !String(customerName || '').trim())
                                    }
                                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 rounded-lg font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                                >
                                    {isProcessingOrder ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                            Processing...
                                        </span>
                                    ) : (
                                        'Place Order'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* Coupon Selection Modal */}
            <AnimatePresence>
                {showCouponModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                        onClick={() => setShowCouponModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-2xl font-bold text-gray-800">Select Coupon</h3>
                                <button
                                    onClick={() => setShowCouponModal(false)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition"
                                >
                                    <IoClose size={24} />
                                </button>
                            </div>

                            {availableCoupons.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <p className="text-lg font-semibold">No Offline coupons available</p>
                                    <p className="text-sm mt-2">Create Offline coupons from the Coupons page</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {availableCoupons.map((coupon) => (
                                        <div
                                            key={coupon._id}
                                            className="border-2 border-gray-200 rounded-lg p-4 hover:border-indigo-500 transition cursor-pointer"
                                            onClick={() => applyCoupon(coupon)}
                                        >
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h4 className="font-bold text-lg text-gray-800">{coupon.name}</h4>
                                                    <p className="text-sm text-gray-600 mt-1">{coupon.description}</p>
                                                </div>
                                                <div className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-bold">
                                                    {coupon.couponCode}
                                                </div>
                                            </div>
                                            <div className="flex gap-4 text-sm text-gray-600 mt-3 flex-wrap">
                                                <span>Min Order: ₹{formatIndianNumber(coupon.minOrderValue || 0)}</span>
                                                <span>•</span>
                                                <span>
                                                    {coupon.discountType === 'percentage'
                                                        ? `${coupon.discountValue}% OFF`
                                                        : `₹${formatIndianNumber(coupon.discountValue)} OFF`}
                                                </span>
                                                {coupon.maxDiscountValue && (
                                                    <>
                                                        <span>•</span>
                                                        <span>Max: ₹{formatIndianNumber(coupon.maxDiscountValue)}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Product Option Selection Modal — always above Find Item modal */}
            <AnimatePresence>
                {showProductModal && selectedProduct && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-slate-900/70 backdrop-blur-[2px] flex items-center justify-center z-[80] p-4"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowProductModal(false);
                            setSelectedProduct(null);
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-5">
                                <h3 className="text-2xl font-bold text-gray-800 pr-2">{selectedProduct.productName}</h3>
                                <button
                                    onClick={() => {
                                        setShowProductModal(false);
                                        setSelectedProduct(null);
                                    }}
                                    className="p-2 hover:bg-gray-100 rounded-full transition shrink-0"
                                >
                                    <IoClose size={24} />
                                </button>
                            </div>

                            <h4 className="font-semibold text-gray-700 mb-3">Select an option:</h4>
                            <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
                                {selectedProduct.productOptions.map((option) => (
                                    <button
                                        key={option._id}
                                        onClick={() => addToCart(selectedProduct, option)}
                                        className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition text-left"
                                    >
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="font-semibold text-gray-800">{getOptionLabel(option)}</p>
                                                {option.discount > 0 && (
                                                    <p className="text-sm text-gray-500 line-through">₹{formatIndianNumber(option.originalPrice)}</p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className="text-lg font-bold text-indigo-600">
                                                    ₹{formatIndianNumber(option.discountPrice || option.originalPrice)}
                                                </p>
                                                {option.discount > 0 && (
                                                    <span className="text-xs text-green-600 font-semibold">
                                                        {option.discount}% OFF
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Find Item Modal — browse by name / category / subcategory */}
            <AnimatePresence>
                {showFindItemModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-3 sm:p-6"
                        onClick={closeFindItemModal}
                    >
                        <motion.div
                            initial={{ scale: 0.94, opacity: 0, y: 16 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.94, opacity: 0, y: 16 }}
                            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col border border-slate-100"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal header */}
                            <div className="relative px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-teal-50 via-white to-emerald-50 border-b border-slate-100">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-[11px] font-bold uppercase tracking-widest text-teal-600 mb-1">Catalogue</p>
                                        <h3 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                                            <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white inline-flex items-center justify-center shadow-md">
                                                <FaSearch className="text-sm" />
                                            </span>
                                            Find Item
                                        </h3>
                                        <p className="text-sm text-slate-500 mt-1">Search by name, or filter by category and subcategory</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={closeFindItemModal}
                                        className="p-2.5 rounded-full hover:bg-slate-100 text-slate-500 transition"
                                        aria-label="Close find item"
                                    >
                                        <IoClose size={22} />
                                    </button>
                                </div>

                                <div className="relative mt-4">
                                    <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        ref={findItemSearchRef}
                                        type="text"
                                        value={findItemSearch}
                                        onChange={(e) => setFindItemSearch(e.target.value)}
                                        placeholder="Search products by name, SKU or barcode..."
                                        className="w-full pl-11 pr-4 py-3.5 rounded-2xl border-2 border-slate-200 bg-white focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 text-[15px] transition"
                                    />
                                </div>
                            </div>

                            {/* Category filters */}
                            <div className="px-5 sm:px-6 py-3 border-b border-slate-100 bg-white">
                                <div className="flex items-center gap-2 overflow-x-auto pos-scroll-x pb-1">
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedParentId(null); setSelectedSubId(null); }}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition border ${
                                            !selectedParentId
                                                ? 'bg-slate-900 text-white border-transparent shadow-sm'
                                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                                        }`}
                                    >
                                        <FaLayerGroup className="text-xs" /> All
                                    </button>
                                    {categories.map((cat) => (
                                        <button
                                            type="button"
                                            key={cat._id}
                                            onClick={() => { setSelectedParentId(cat._id); setSelectedSubId(null); }}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition border ${
                                                String(selectedParentId) === String(cat._id)
                                                    ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white border-transparent shadow-md'
                                                    : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300 hover:text-teal-700'
                                            }`}
                                        >
                                            {cat.imageUrl && (
                                                <img src={cat.imageUrl} alt="" className="h-5 w-5 rounded-full object-cover" />
                                            )}
                                            {cat.categoryName}
                                            {Array.isArray(cat.subcategories) && cat.subcategories.length > 0 && (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                                    String(selectedParentId) === String(cat._id)
                                                        ? 'bg-white/25 text-white'
                                                        : 'bg-slate-100 text-slate-500'
                                                }`}>
                                                    {cat.subcategories.length}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>

                                <AnimatePresence>
                                    {activeSubcategories.length > 0 && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mt-3 pt-3 border-t border-dashed border-slate-200"
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                                    {activeParent?.categoryName}
                                                </span>
                                                <span className="h-px flex-1 bg-slate-100" />
                                                <span className="text-[11px] text-slate-400">Subcategories</span>
                                            </div>
                                            <div className="flex items-center gap-2 overflow-x-auto pos-scroll-x pb-0.5">
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedSubId(null)}
                                                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition border ${
                                                        !selectedSubId
                                                            ? 'bg-teal-50 text-teal-700 border-teal-200'
                                                            : 'bg-white text-slate-500 border-slate-200 hover:border-teal-200'
                                                    }`}
                                                >
                                                    All in {activeParent?.categoryName}
                                                </button>
                                                {activeSubcategories.map((sub) => (
                                                    <button
                                                        type="button"
                                                        key={sub._id}
                                                        onClick={() => setSelectedSubId(sub._id)}
                                                        className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition border ${
                                                            String(selectedSubId) === String(sub._id)
                                                                ? 'bg-teal-600 text-white border-transparent shadow-sm'
                                                                : 'bg-white text-slate-500 border-slate-200 hover:border-teal-200 hover:text-teal-700'
                                                        }`}
                                                    >
                                                        {sub.categoryName}
                                                    </button>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Products grid */}
                            <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 pos-scroll-light min-h-[280px]">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-semibold text-slate-600 flex items-center gap-2">
                                        <FaBoxOpen className="text-teal-500" />
                                        {findItemTotal.toLocaleString('en-IN')} product{findItemTotal === 1 ? '' : 's'}
                                    </p>
                                    {findItemTotalPages > 1 && (
                                        <p className="text-xs text-slate-400">Page {findItemPage + 1} / {findItemTotalPages}</p>
                                    )}
                                </div>

                                {loadingFindItems ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {Array.from({ length: 8 }).map((_, i) => (
                                            <div key={i} className="animate-pulse rounded-2xl border border-slate-100 bg-slate-50 h-44" />
                                        ))}
                                    </div>
                                ) : findItemProducts.length === 0 ? (
                                    <div className="text-center py-16">
                                        <FaBoxOpen className="text-5xl text-slate-200 mx-auto mb-3" />
                                        <p className="text-lg font-semibold text-slate-500">No products found</p>
                                        <p className="text-sm text-slate-400 mt-1">Try another search or category</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {findItemProducts.map((product, idx) => {
                                            const price = getDisplayPrice(product);
                                            const stock = getDisplayStock(product);
                                            const outOfStock = product.isOutOfStock === true || stock <= 0;
                                            return (
                                                <motion.button
                                                    key={`${product._id}-${idx}`}
                                                    type="button"
                                                    initial={{ opacity: 0, y: 8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.18, delay: Math.min(idx, 10) * 0.02 }}
                                                    whileHover={outOfStock ? undefined : { y: -3 }}
                                                    onClick={() => !outOfStock && handleFindItemSelect(product)}
                                                    disabled={outOfStock}
                                                    className={`group relative text-left rounded-2xl border overflow-hidden transition ${
                                                        outOfStock
                                                            ? 'opacity-55 cursor-not-allowed border-slate-100 bg-slate-50'
                                                            : 'border-slate-200 bg-white hover:border-teal-300 hover:shadow-lg hover:shadow-teal-500/10'
                                                    }`}
                                                >
                                                    <div className="relative h-28 w-full overflow-hidden bg-slate-100">
                                                        <img
                                                            src={getProductImage(product)}
                                                            alt={product.productName}
                                                            onError={handleProductImageError}
                                                            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                        />
                                                        {!outOfStock && (
                                                            <span className="absolute top-2 right-2 h-8 w-8 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition">
                                                                <FaPlus className="text-white text-xs" />
                                                            </span>
                                                        )}
                                                        {outOfStock && (
                                                            <span className="absolute inset-0 flex items-center justify-center bg-slate-900/45 text-[11px] font-bold text-white tracking-wide">
                                                                OUT OF STOCK
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="p-3">
                                                        <p className="text-sm font-semibold text-slate-800 line-clamp-2 leading-snug min-h-[2.5rem]">
                                                            {product.productName}
                                                        </p>
                                                        <div className="flex items-center justify-between mt-2">
                                                            <span className="text-base font-bold text-teal-600">
                                                                ₹{formatIndianNumber(price).replace('.00', '')}
                                                            </span>
                                                            <span className={`text-[10px] font-semibold ${stock <= 5 ? 'text-amber-500' : 'text-emerald-600'}`}>
                                                                {formatStockDisplay(product)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </motion.button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Pagination footer */}
                            {findItemTotalPages > 1 && (
                                <div className="px-5 sm:px-6 py-3 border-t border-slate-100 bg-slate-50/80 flex items-center justify-between gap-3">
                                    <button
                                        type="button"
                                        disabled={findItemPage <= 0 || loadingFindItems}
                                        onClick={() => loadFindItemPage(findItemPage - 1)}
                                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-600 hover:border-teal-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                    >
                                        <FaChevronLeft className="text-xs" /> Prev
                                    </button>
                                    <span className="text-xs font-medium text-slate-500">
                                        Showing {findItemPage * FIND_ITEM_PAGE_SIZE + 1}–{Math.min((findItemPage + 1) * FIND_ITEM_PAGE_SIZE, findItemTotal)} of {findItemTotal}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={findItemPage >= findItemTotalPages - 1 || loadingFindItems}
                                        onClick={() => loadFindItemPage(findItemPage + 1)}
                                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-600 hover:border-teal-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                    >
                                        Next <FaChevronRight className="text-xs" />
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default MerchantBilling;
